import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Typography } from '@material-ui/core';
import { FormHandles } from '@unform/core';
import { Form as UForm } from '@unform/web';
import {
  Button,
  ButtonIcon,
  Input,
  Select, WrapperLabel
} from 'components/Forms';
import Layout from 'components/Layout';
import { useHttp } from 'hooks/http';
import { useToast } from 'hooks/toast';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { EventNoteIcon, PlusIcon, TrashIcon } from 'styles/icons';
import getValidationError from 'utils/getValidationsErros';
import { bairrosRJ, municipiosRJ, regioesRJ } from 'utils/regioesRJ';
import * as Yup from 'yup';
import {
  Container, Content, GridButtons, WrapperBtnCancel, WrapperBtnSubmit,
} from './styles';

interface IServiceRow {
  partner: { value: string; label: string };
  service: { value: string; label: string };
  quantity: any;
  services: Array<{ value: string; label: string }>;
}

interface ConfirmedService {
  partner_name: string;
  service_name: string;
  quantity: number | string;
}

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const formRef = useRef<FormHandles>(null);
  const { httpGet, httpPut, loading } = useHttp();
  const { addToast } = useToast();
  const [btnLoading, setBtnLoading] = useState(false);
  const [partners, setPartners] = useState<Array<{ value: string; label: string }>>([]);
  const [bairro, setBairro] = useState('');
  const [isBairroDisabled, setIsBairroDisabled] = useState(true);
  const [bairroOptions, setBairroOptions] = useState<{ value: string; label: string }[]>([]);
  const [filteredMunicipios, setFilteredMunicipios] = useState<{ value: string; label: string; region: string }[]>([]);
  // const [selectedRegiao, setSelectedRegiao] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<{ [partnerId: string]: string[] }>({});
  const [selectedMunicipio, setSelectedMunicipio] = useState<string | null>(null);
  const [serviceRows, setServiceRows] = useState<IServiceRow[]>([]);
  const [actionStatus] = useState([
    { value: true, label: 'Ativada' },
    { value: false, label: 'Desativada' },
  ]);
  const [newServiceRow, setNewServiceRow] = useState<IServiceRow>({
    partner: { value: '', label: '' },
    service: { value: '', label: '' },
    quantity: '',
    services: [],
  });
  const [originalDetails, setOriginalDetails] = useState<any[]>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);

  function formatDateForInput(dateString: string): string {
    const date = new Date(dateString);

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    async function loadActionDetails(): Promise<void> {
      const { status, data } = await httpGet(`/social-action/${id}`);
      if (status === 200) {
        const actionDetails = data;

        const formattedDate = formatDateForInput(actionDetails.data_acao);
        const statusOption = actionStatus.find(opt => opt.value === actionDetails.action_status);

        formRef.current?.setData({
          data_acao: formattedDate,
          regiao: { value: actionDetails.regiao, label: actionDetails.regiao },
          municipio: { value: actionDetails.municipio, label: actionDetails.municipio },
          bairro: { value: actionDetails.bairro, label: actionDetails.bairro },
          action_status: statusOption || null,
          attendance_count: actionDetails.attendance_count,
        });

        const rows = actionDetails.details.map((detail: any) => ({
          partner: { value: detail.partner_id, label: detail.partner_name },
          service: { value: detail.service_id, label: detail.service_name },
          quantity: detail.quantity,
          services: [],
        }));

        setServiceRows(rows);
        setOriginalDetails(actionDetails.details);

      }
    }

    loadActionDetails();
  }, [id, httpGet, actionStatus]);

  useEffect(() => {
    async function loadPartners(): Promise<void> {
      const { status, data } = await httpGet('social-action/partners/all');
      if (status === 200) {
        const stations = data
          ?.map((station: any) => ({
            value: station.id,
            label: `${station.partner_name}`,
          }));

        setPartners(stations);
      }
    }

    loadPartners();
  }, [httpGet]);

  const loadServicesByPartner = async (
    partnerId: string,
    index: number = -1
  ): Promise<{ value: string; label: string }[]> => {
    const { status, data } = await httpGet(`social-action/partners-services/${partnerId}/services`);

    if (status === 200) {
      const servicesData = data.map((service: any) => ({
        value: service.id,
        label: service.service_name,
      }));

      const filteredServices = servicesData.filter(
        (service: { value: string }) => !selectedServices[partnerId]?.includes(service.value),
      );

      // Se for uma linha já existente
      if (index >= 0) {
        const updatedRows = [...serviceRows];
        updatedRows[index].services = filteredServices;
        setServiceRows(updatedRows);
      }

      return filteredServices;
    }

    return [];
  };

  const handleRemoveServiceRow = (index: number) => {
    const updatedRows = serviceRows.filter((_, rowIndex) => rowIndex !== index);

    const partnerId = serviceRows[index].partner.value;
    const serviceId = serviceRows[index].service.value;

    setSelectedServices((prev) => {
      const updatedSelectedServices = { ...prev };
      if (updatedSelectedServices[partnerId]) {
        updatedSelectedServices[partnerId] = updatedSelectedServices[partnerId].filter(
          (id) => id !== serviceId,
        );
      }
      return updatedSelectedServices;
    });

    setServiceRows(updatedRows);
  };

  const handleMunicipioChange = (selectedOption: any) => {
    const municipioSelecionado = selectedOption.value;
    setSelectedMunicipio(municipioSelecionado);

    const municipioInfo = municipiosRJ.find(m => m.value === municipioSelecionado);
    const regiaoInfo = municipioInfo?.region ?? '';

    formRef.current?.setFieldValue('regiao', { value: regiaoInfo, label: regiaoInfo });

    if (municipioSelecionado === 'Rio de Janeiro') {
      setBairroOptions(bairrosRJ);
      setIsBairroDisabled(false);
      setBairro('');
      formRef.current?.setFieldValue('bairro', { value: '', label: '' });
    } else {
      const foraDaCapital = { value: 'Fora da Capital', label: 'Fora da Capital' };
      setBairro('Fora da Capital');
      setIsBairroDisabled(true);
      formRef.current?.setFieldValue('bairro', foraDaCapital);
    }

    formRef.current?.setFieldError('bairro', '');
  };

  const removed_services = originalDetails.filter(orig => {
    return !serviceRows.some(row =>
      row.partner.value === orig.partner_id &&
      row.service.value === orig.service_id
    );
  });

  const handleSubmit = useCallback(async (data: any) => {
    try {

      setBtnLoading(true);

      const bairroValue = selectedMunicipio === 'Rio de Janeiro' ? data.bairro?.value || '' : 'Fora da Capital';

      const _socialAction = {
        ...data,
        regiao: data.regiao.value,
        municipio: data.municipio.value,
        bairro: bairroValue,
        action_status: data.action_status?.value,
        services: serviceRows.map((row: IServiceRow) => ({
          partner_id: row.partner.value,
          partner_name: row.partner.label,
          service_id: row.service.value,
          service_name: row.service.label,
          quantity: row.quantity,
        })),
        removed_services: removed_services.map(({ partner_id, service_id }) => ({
          partner_id,
          service_id
        }))
      };

      const schema = Yup.object().shape({
        services: Yup.array().of(
          Yup.object().shape({
            quantity: Yup.number()
              .positive('A quantidade deve ser um número positivo')
              .required('A quantidade é obrigatória'),
          }),
        ),
      });

      formRef.current?.setErrors({});
      await schema.validate(_socialAction, { abortEarly: false });

      setFormData(_socialAction);
      setIsConfirmDialogOpen(true);

      setBtnLoading(false);
    } catch (err) {
      setBtnLoading(false);
      if (err instanceof Yup.ValidationError) {
        const erros = getValidationError(err);
        formRef.current?.setErrors(erros);
      } else {
        addToast({ title: 'Erro ao validar a ação social!', type: 'error' });
      }
    }
  }, [addToast, selectedMunicipio, serviceRows, removed_services]);

  const submitConfirmedData = async () => {
    try {
      setBtnLoading(true);

      const { services, removed_services, ...otherData } = formData;

      const formattedServices = services.map((s: any) => ({
        partner_id: s.partner_id,
        service_id: s.service_id,
        quantity: s.quantity,
      }));

      const payload = {
        ...otherData,
        services: formattedServices,
        removed_services,
      };

      const { status } = await httpPut(`social-action/edit/${id}`, payload);

      setBtnLoading(false);
      setIsConfirmDialogOpen(false);

      if (status === 200) {
        addToast({ title: 'Salvo com sucesso!', type: 'success' });
        history.push('/social-action/list');
      } else {
        addToast({ title: 'Não foi possível gravar!', type: 'error' });
      }
    } catch (error) {
      setBtnLoading(false);
      addToast({ title: 'Erro ao salvar a ação social!', type: 'error' });
    }
  };

  useEffect(() => {
    setFilteredMunicipios(municipiosRJ);
  }, []);

  const breadcrumbs = {
    icon: EventNoteIcon,
    links: [{ path: '', title: 'Ação Social' }],
  };

  return (
    <Layout breadcrumbs={breadcrumbs} loading={loading}>
      <Container>
        <UForm ref={formRef} onSubmit={handleSubmit}>
          <Content>
            <Grid container spacing={1}>
              <WrapperLabel label="Data da ação" xs={12} md={6} sm={6}>
                <Input
                  name="data_acao"
                  type="date"
                />
              </WrapperLabel>
              <WrapperLabel label="Município" xs={12} md={6} sm={6}>
                <Select
                  name="municipio"
                  options={filteredMunicipios}
                  value={selectedMunicipio ? { value: selectedMunicipio, label: selectedMunicipio } : null}
                  onChange={handleMunicipioChange}
                />
              </WrapperLabel>
              <WrapperLabel label="Região" xs={12} md={6} sm={6}>
                <Select
                  name="regiao"
                  options={regioesRJ}
                  isDisabled
                />
              </WrapperLabel>
              <WrapperLabel label="Bairro" xs={12} md={6} sm={6}>
                <Select
                  name="bairro"
                  options={bairroOptions}
                  value={bairro ? { value: bairro, label: bairro } : null}
                  onChange={(e: any) => setBairro(e.value)}
                  isDisabled={isBairroDisabled || !selectedMunicipio}
                />
              </WrapperLabel>
              <WrapperLabel label="Quantitativo de público presente" xs={12} md={6} sm={6}>
                <Input
                  name='attendance_count'
                />
              </WrapperLabel>
              <WrapperLabel label="Status" xs={12} md={6} sm={6}>
                <Select
                  name='action_status'
                  options={actionStatus}
                />
              </WrapperLabel>
              <WrapperLabel label="Parceiro" xs={12} md={6} sm={6} lg={4}>
                <Select
                  name="new_service_partner"
                  value={newServiceRow.partner}
                  options={partners}
                  onChange={async (selectedOption: any) => {
                    if (!selectedOption) return;

                    const services = await loadServicesByPartner(selectedOption.value);
                    setNewServiceRow({
                      partner: selectedOption,
                      service: { value: '', label: '' },
                      quantity: '',
                      services: services,
                    });
                  }}
                />
              </WrapperLabel>
              <WrapperLabel label="Serviço" xs={12} md={6} sm={6} lg={4}>
                <Select
                  name="new_service_service"
                  value={newServiceRow.service}
                  options={newServiceRow.services}
                  onChange={(selectedOption: any) =>
                    setNewServiceRow((prev) => ({ ...prev, service: selectedOption }))
                  }
                />
              </WrapperLabel>
              <WrapperLabel label="Quantidade" xs={12} md={12} sm={12} lg={4}>
                <Input
                  name="new_service_quantity"
                  value={newServiceRow?.quantity || ''}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    setNewServiceRow((prev) => ({ ...prev, quantity: value }));
                  }}
                />
                <ButtonIcon
                  title="Adicionar Serviço"
                  onClick={() => {
                    if (
                      newServiceRow.partner.value &&
                      newServiceRow.service.value &&
                      newServiceRow.quantity
                    ) {
                      const alreadyExists = serviceRows.some(
                        (row) =>
                          row.partner.value === newServiceRow.partner.value &&
                          row.service.value === newServiceRow.service.value
                      );

                      if (alreadyExists) {
                        addToast({
                          type: 'error',
                          title: 'Esse parceiro já possui esse serviço cadastrado!',
                        });
                        return;
                      }

                      setServiceRows((prev) => [...prev, newServiceRow]);
                      setNewServiceRow({
                        partner: { value: '', label: '' },
                        service: { value: '', label: '' },
                        quantity: '',
                        services: [],
                      });
                    }
                  }}
                >
                  <PlusIcon />
                </ButtonIcon>
              </WrapperLabel>
            </Grid>
          </Content>
          <div style={{ marginTop: '1.5rem' }} />
          <Content>
            {serviceRows.length > 0 && (
              <div
                style={{
                  width: '100%',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 20,
                  marginTop: 20,
                  backgroundColor: '#FFF7ED',
                }}
              >
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, marginBottom: 12, color: '#F97316' }}
                >
                  Relatório Geral
                </Typography>

                {Object.entries(
                  serviceRows.reduce((acc: Record<string, number>, item) => {
                    const partnerName = item.partner?.label || 'Parceiro não identificado';
                    const quantity = Number(item.quantity) || 0;
                    acc[partnerName] = (acc[partnerName] || 0) + quantity;
                    return acc;
                  }, {})
                )
                  .reverse()
                  .map(([partnerName, total]) => (
                    <div
                      key={partnerName}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: '1px solid #FFE4C7',
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 500,
                          flex: 1,
                          marginRight: 30,
                          wordBreak: 'break-word',
                        }}
                      >
                        {partnerName}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 700, minWidth: 30, textAlign: 'right' }}
                      >
                        {total}
                      </Typography>
                    </div>
                  ))}
              </div>
            )}
          </Content>
          <div style={{ marginTop: '1rem' }} />
          <Content>
            <Grid container spacing={1}>
              {(() => {
                const grouped = serviceRows.reduce<Record<string, IServiceRow[]>>((acc, item) => {
                  const partnerName = item.partner?.label || 'Parceiro não identificado';
                  if (!acc[partnerName]) {
                    acc[partnerName] = [];
                  }
                  acc[partnerName].push(item);
                  return acc;
                }, {});

                const rankedPartners = Object.entries(grouped)
                  .map(([partnerName, rows]) => ({
                    partnerName,
                    total: rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0),
                    rows,
                  }))
                  .reverse();

                return rankedPartners.map(({ partnerName, rows }, index) => (
                  <div key={partnerName} style={{ marginBottom: 20, width: '100%' }}>
                    <Typography
                      style={{
                        fontWeight: 'bold',
                        marginBottom: 8,
                        fontSize: 16,
                      }}
                    >
                      {`${rankedPartners.length - index} - ${partnerName}`}
                    </Typography>

                    {rows.map((row, serviceIndex) => (
                      <Grid
                        container
                        spacing={1}
                        key={serviceIndex}
                        style={{
                          marginBottom: serviceIndex === rows.length - 1 ? 0 : 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Grid item xs={9}>
                          <Typography
                            style={{
                              fontSize: 13,
                              color: '#555',
                              marginBottom: 2,
                            }}
                          >
                            Serviço
                          </Typography>
                          <Typography>{row.service?.label}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography
                            style={{
                              fontSize: 13,
                              color: '#555',
                              marginBottom: 2,
                            }}
                          >
                            Quantidade
                          </Typography>
                          <Typography>{row.quantity}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                          <TrashIcon
                            onClick={() => handleRemoveServiceRow(serviceRows.indexOf(row))}
                            color="red"
                            cursor="pointer"
                            title="Remover"
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </div>
                ));
              })()}
            </Grid>

            <GridButtons style={{ marginTop: 15 }}>
              <WrapperBtnCancel>
                <Button
                  type="button"
                  onClick={() => {
                    history.push('/social-action/list');
                  }}
                >
                  Cancelar
                </Button>
              </WrapperBtnCancel>
              <WrapperBtnSubmit>
                <Button type="submit" isLoading={btnLoading}>
                  Enviar
                </Button>
              </WrapperBtnSubmit>
            </GridButtons>
          </Content>
        </UForm>
      </Container>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography style={{ fontWeight: 'bold', color: '#F97316' }}>
            Confirmação da Ação Social
          </Typography>
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: '#FFF7ED' }}>
          <Grid container spacing={3} style={{ paddingBottom: 20 }}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Data da Ação</Typography>
              <Typography variant="body1">{formData?.data_acao}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Município</Typography>
              <Typography variant="body1">
                {formData?.municipio}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Região</Typography>
              <Typography variant="body1">
                {formData?.regiao}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Bairro</Typography>
              <Typography variant="body1">
                {formData?.bairro}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Público Presente</Typography>
              <Typography variant="body1">
                {formData?.attendance_count || "Não cadastrado"}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2" color="textSecondary">Status</Typography>
              <Typography variant="body1">
                {formData?.action_status ? 'Ativado' : 'Desativado'}
              </Typography>
            </Grid>

          </Grid>


          <Divider style={{ margin: '20px 0' }} />

          <Grid container spacing={3} style={{ paddingBottom: 20 }}>
            <Grid item xs={6}>
              <div style={{
                backgroundColor: '#FFEDD5',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#EA580C', marginBottom: 2 }}>
                  {formData?.services ? new Set(formData.services.map((s: any) => s.partner_name)).size : 0}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Parceiros
                </Typography>
              </div>
            </Grid>

            <Grid item xs={6}>
              <div style={{
                backgroundColor: '#FFEDD5',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <Typography variant="h4" style={{ fontWeight: 'bold', color: '#EA580C', marginBottom: 2 }}>
                  {formData?.services?.reduce((acc: number, cur: any) => acc + Number(cur.quantity), 0)}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Serviços
                </Typography>
              </div>
            </Grid>
          </Grid>

          <Divider style={{ marginBottom: 20 }} />

          {(() => {
            const grouped = (formData?.services as ConfirmedService[] || []).reduce<Record<string, ConfirmedService[]>>(
              (acc, service) => {
                const partnerName = service.partner_name || 'Parceiro não identificado';
                if (!acc[partnerName]) {
                  acc[partnerName] = [];
                }
                acc[partnerName].push(service);
                return acc;
              },
              {}
            );

            return Object.entries(grouped).reverse().map(([partnerName, services], groupIndex) => (
              <div key={partnerName} style={{ marginBottom: 20 }}>
                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
                  <Grid item>
                    <Typography style={{
                      backgroundColor: '#F97316',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 30,
                      height: 30,
                      textAlign: 'center',
                      lineHeight: '30px',
                      fontWeight: 'bold',
                    }}>
                      {groupIndex + 1}
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {partnerName} — {services.reduce((acc, s) => acc + Number(s.quantity || 0), 0)} atendimentos
                    </Typography>
                  </Grid>
                </Grid>

                {services.reverse().map((service, serviceIndex) => (
                  <Grid
                    container
                    key={serviceIndex}
                    spacing={2}
                    style={{
                      marginBottom: serviceIndex === services.length - 1 ? 0 : 12,
                      backgroundColor: '#FFEDD5',
                      borderRadius: 8,
                      padding: 10,
                      paddingLeft: 45,
                    }}
                  >
                    <Grid item xs={9}>
                      <Typography style={{ color: '#EA580C', fontWeight: 600 }}>
                        {service.service_name}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography style={{
                        color: '#EA580C',
                        borderRadius: '50%',
                        width: 30,
                        height: 30,
                        textAlign: 'center',
                        lineHeight: '30px',
                        fontWeight: 'bold',
                        float: 'right',
                        marginTop: '4px',
                      }}>
                        {service.quantity}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </div>
            ));
          })()}

        </DialogContent>

        <DialogActions style={{ padding: 16 }}>
          <Button onClick={() => setIsConfirmDialogOpen(false)}>
            Voltar para Edição
          </Button>
          <Button onClick={submitConfirmedData} isLoading={btnLoading} style={{ backgroundColor: '#F97316', color: '#fff' }}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Profile;